import { useState, useEffect, useMemo } from "react";

import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "V2.0/common/dialog";
import { Button } from "V2.0/common/button";
import { useForm } from "V2.0/HomePages/Assistants/AssistantTools/CustomAPIs/components/Form/useForm";
import Form from "V2.0/HomePages/Assistants/AssistantTools/CustomAPIs/components/Form";
import Joi from "joi-browser";
import { getLifecycleCbs } from "./common/lifecycleCbs";
import { CampaignTypeIcons } from "../../common/CampaignTypeIcons";
import { CAMPAIGN_TYPE } from "../../BulkCampaigns/BulkCampaignCreate";
import { getFollowUpSettings } from "./common/utils";

const schema = {
  followUpDelay: Joi.number()
    .required()
    .label("Follow-up after (hours)")
    .error((errors) => {
      errors.forEach((err) => {
        switch (err.code) {
          case "number.base":
            err.message = "Follow-up after (hours) should be a number field";
            break;
          case "any.required":
            err.message = "Follow-up after (hours) is required";
            break;
        }
      });
      return errors;
    }),
  followUpTemplate: Joi.object({
    name: Joi.string().required(),
    language: Joi.string().allow(null, ""),
  }).label("Follow Up template"),
};

export default function FollowUpWhatsApp({
  followUpSettings,
  setFollowUpSettings,
  itemIndexForEdit,
  messageTemplates,
  handleSave,
  open,
  onClose,
  mode,
}) {
  const [inputField] = useState(
    getFollowUpSettings(
      mode,
      itemIndexForEdit,
      followUpSettings,
      CAMPAIGN_TYPE.WHATSAPP
    )
  );
  const [loading, setLoading] = useState(false);

  const { data, validate, handleChange, error } = useForm(inputField, schema);

  const template = useMemo(
    () => [
      {
        name: "followUpDelay",
        label: "Follow-up after (hours) *",
        type: "text",
      },
      {
        name: "followUpTemplate",
        label: "Whatsapp Message Template *",
        type: "select",
        options: [
          { id: 0, value: JSON.stringify({}), label: "(No template selected)" },
        ].concat(
          messageTemplates.map((messageTemplate, index) => ({
            id: index,
            value: JSON.stringify(messageTemplate),
            label: messageTemplate.name,
          }))
        ),
        value: JSON.parse(inputField.followUpTemplate).name,
      },
    ],
    [messageTemplates]
  );

  const handleSubmit = async () => {
    let updatedFollowUps = [];

    const { followUpDelay, followUpTemplate } = data;

    const followUpTemplateObject = JSON.parse(followUpTemplate);

    const updatedData = {
      followUpDelay,
      followUpMessage: "",
      whatsAppFollowUp: {
        templateName: followUpTemplateObject.name,
        language: followUpTemplateObject.language,
      },
    };

    if (mode === "add") {
      const addedFollowUp = { ...updatedData, order: followUpSettings.length };
      updatedFollowUps = [...followUpSettings, addedFollowUp];
    }

    if (mode === "edit") {
      const editedFollowUp = { ...updatedData, order: itemIndexForEdit };

      updatedFollowUps = followUpSettings.toSpliced(
        itemIndexForEdit,
        1,
        editedFollowUp
      );
    }

    const onInit = () => setLoading(true);

    const onError = () => setLoading(false);

    const onSuccess = () => {
      setFollowUpSettings(updatedFollowUps);
      setLoading(false);
      onClose();
    };
    const lifecycleCbs = getLifecycleCbs(onInit, onSuccess, onError);

    handleSave({ followUpSettings: updatedFollowUps }, lifecycleCbs);
  };

  const isSaveDisabled =
    Boolean(Object.keys(error).length) || validate(data, schema);

  return (
    <Dialog
      open={open}
      onClose={onClose}
    >
      <DialogTitle>
        <div className="flex gap-x-2 items-center">
          {CampaignTypeIcons[CAMPAIGN_TYPE.WHATSAPP]} Follow-Up WhatsApp
        </div>
      </DialogTitle>
      <DialogBody>
        <Form
          data={data}
          handleChange={handleChange}
          template={template}
          error={error}
        />
      </DialogBody>
      <DialogActions>
        <Button
          outline
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          disabled={isSaveDisabled}
          loading={loading}
          onClick={handleSubmit}
        >
          {mode === "edit" ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
